import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useMemo, useState } from 'react';
import CustomDateInput from '@Components/Common/Time/CustomDateInput';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

interface DayPickerProps {
  data: {
    from: string;
    to: string;
  };
  setFromTo: (from: string, to: string) => void;
  onClose: () => void;
}

const inputFormat = 'yyyy-MM-dd';

const getToMaxDate = (startDate: string) =>
  new Date(
    Math.min(
      dayjs().utc(true).toDate().getTime(),
      dayjs.tz(startDate, '+00:00').add(30, 'day').toDate().getTime()
    )
  );

export const DayPicker = ({ data, setFromTo, onClose }: DayPickerProps) => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  const [startDate, setStartDate] = useState<string>(data.from);
  const [endDate, setEndDate] = useState<string>(data.to);

  const fromMinMaxDate = useMemo(() => {
    const utcNow = dayjs().utc(true);
    return {
      minDate: utcNow.add(-179, 'day').toDate(),
      maxDate: utcNow.toDate(),
    };
  }, []);

  const toMinMaxDate = useMemo(() => {
    return {
      minDate: dayjs.tz(startDate, '+00:00').toDate(),
      maxDate: getToMaxDate(startDate),
    };
  }, [startDate]);

  const handleStartDateChange = (value: Date) => {
    const newStartDate = formatDate(value);
    setStartDate(newStartDate);
    const diff = dayjs.tz(endDate, '+00:00').diff(dayjs(value), 'day');
    if (diff > 30) {
      setEndDate(formatDate(getToMaxDate(newStartDate)));
    } else if (diff < 0) {
      setEndDate(newStartDate);
    }
  };

  const handleEndDateChange = (value: Date) => {
    setEndDate(formatDate(value));
  };

  const handleApplyClick = () => {
    setFromTo(startDate, endDate);
    onClose();
  };

  return (
    <Box
      backgroundColor={colorStyle.background.tertiary}
      position="absolute"
      left="100%"
      top={260}
      px="3x"
      py="3x"
      borderLeft={1}
      borderColor={colorStyle.divider}
      width="250px"
    >
      <Flex alignItems="center" columnGap="3x" mb="6x">
        <Text fontSize="md" lineHeight="md">
          {t('reportCommon.period.label.customPeriod')}
        </Text>
      </Flex>
      <Grid templateColumns="auto 1fr" alignItems="center" rowGap="3x" mb="3x">
        <Text minWidth="16x">{t('common.timeRange.from')}:</Text>
        <Flex>
          <CustomDateInput
            inputFormat={inputFormat}
            defaultValue={startDate}
            onChange={handleStartDateChange}
            {...fromMinMaxDate}
          />
        </Flex>
        <Text minWidth="16x">{t('common.timeRange.to')}:</Text>
        <Flex>
          <CustomDateInput
            key={endDate}
            inputFormat={inputFormat}
            defaultValue={endDate}
            onChange={handleEndDateChange}
            {...toMinMaxDate}
          />
        </Flex>
      </Grid>
      <Flex justifyContent="flex-end">
        <Grid templateColumns="1fr 1fr" columnGap="2x">
          <Button variant="primary" onClick={handleApplyClick}>
            {t('common.button.apply')}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </Grid>
      </Flex>
    </Box>
  );
};

const formatDate = (date: Date) => {
  return dayjs(date).format('YYYY-MM-DD');
};
